import React, { ElementType, ReactNode } from "react"
import { Grid2, List, ListItemButton, ListItemIcon, ListItemText, PopperPlacementType } from "@mui/material"

import { Text } from "@/components/Text"

import { FeatureStatusChip } from "@/components/Sidebar/FeatureStatusChip"
import { NavPopper } from "@/components/Sidebar/NavPopper"
import { FeatureType, MenuItem } from "@/components/Sidebar/protocols"
import useStyles from "@/components/Sidebar/NavItem/styles"

type NavItemVariant = "inbox" | "console"
type NavItemTone = "highlighted" | "muted"

type NavItemProps = {
	icon: ReactNode
	label: string
	isActive?: boolean
	isCollapsed?: boolean
	isDisabled?: boolean
	tone?: NavItemTone
	featureType?: FeatureType
	href?: string
	component?: ElementType
	onClick?: () => void
	variant?: NavItemVariant
	placement?: PopperPlacementType
	items?: MenuItem[]
}

export const NavItem = ({ icon, label, isActive, isCollapsed, isDisabled, tone, featureType, href, component, onClick, variant = "inbox", placement, items }: NavItemProps) => {
	const classes = useStyles()
	const isConsole = variant === "console"

	const navItemTone = {
		highlighted: classes.navItemHighlighted,
		muted: classes.navItemMuted
	}

	const navItemSx = [
		classes.navItem,
		isCollapsed && classes.navItemCollapsed,
		isConsole ? classes.navItemSecondary : classes.navItemPrimary,
		isActive && (isConsole ? classes.navItemActiveSecondary : classes.navItemActivePrimary),
		tone && navItemTone[tone]
	]

	const labelSx = [classes.navItemLabel, isCollapsed ? classes.navItemLabelHidden : classes.navItemLabelVisible]

	const iconSx = [classes.navItemIcon, isCollapsed && classes.navItemIconCollapsed]

	const linkProps = {
		...(href ? { href } : {}),
		...(component ? { component } : {})
	}

	const trigger = (
		<ListItemButton disableGutters disabled={isDisabled} onClick={onClick} sx={navItemSx} {...linkProps}>
			<ListItemIcon sx={iconSx}>{icon}</ListItemIcon>
			<ListItemText
				disableTypography
				sx={labelSx}
				primary={
					<Grid2 component="span" sx={classes.navItemLabelRow}>
						<Text.Body type="small1" sx={{ color: "inherit" }}>{label}</Text.Body>
						{featureType && !isCollapsed && <FeatureStatusChip type={featureType} />}
					</Grid2>
				}
			/>
		</ListItemButton>
	)

	if (!items?.length) {
		return trigger
	}

	return (
		<NavPopper
			placement={placement ?? "right-start"}
			panelContent={
				<List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
					{items.map((item) => (
						<NavItem
							key={item.label}
							icon={item.icon}
							label={item.label}
							isDisabled={item.isDisabled}
							isActive={item.isActive}
							tone={item.tone}
							featureType={item.featureType}
							href={item.href}
							variant={variant}
							onClick={item.onClick}
						/>
					))}
				</List>
			}
		>
			{trigger}
		</NavPopper>
	)
}
